import { useState, forwardRef } from 'react';
import { DEFAULT_DELAY } from 'utility/config';
import {
  StyledUnderlinedLabelWrapper as StyledLabelWrapper,
  StyledUnderlinedLabel as StyledLabel,
  StyledUnderlinedInputWrapper as StyledInputWrapper,
  StyledUnderlinedInputEndcap as StyledInputEndcap,
  StyledUnderlinedInput as StyledInput,
  StyledUnderlinedNumberInput as StyledNumberInput,
  INPUT_TYPES,
} from './styles';
import {
  getInputType,
  validateEmail,
  validateNumber,
  hasValue,
} from './helpers';
import Icon from '../Icon';
import { useTruncationTooltip } from '../Tooltip';

const Input = forwardRef(
  (
    {
      error = false,
      className = '',
      label = null,
      value = null,
      placeholder = '',
      onChange = () => {},
      type = INPUT_TYPES.TEXT,
      disabled = false,
      underline = false,
      pill = false,
      id = '',
      inputType = null,
      endcap = null,
      onClick = null,
      onFocus = null,
      onBlur = null,
      hideLabel = false,
      hideIcon = false,
      InputLabelProps,
      currencyCharacter = '$',
      percentageCharacter = '%',
      ...others
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const [tooltipProps, tooltip] = useTruncationTooltip();
    const isFilled = hasValue(value) && value !== '';
    // If the input has an icon at the front, put the label in the shrink state to avoid a clash
    const isAdornedStart = type === INPUT_TYPES.PRICE;
    const [numberErrorClass, setNumberErrorClass] = useState('');
    const [emailErrorClass, setEmailErrorClass] = useState('');
    const idAttr = id ? { id } : {};
    const isShrunk =
      isFilled ||
      focused ||
      isAdornedStart ||
      (InputLabelProps && InputLabelProps.shrink);

    // When time allows, moving renderInputField() to helpers could DRY up the Outlined/Underlined files significantly
    const renderInputField = () => {
      const commonInputProps = {
        value,
        onChange: (event) => {
          if (
            type === INPUT_TYPES.NUMBER &&
            !validateNumber(event.target.value)
          ) {
            event.preventDefault();
            setNumberErrorClass('number-error');
            setTimeout(
              () => setNumberErrorClass('no-number-error'),
              DEFAULT_DELAY
            );
            const { value: val } = event.target;
            onChange(val.substring(0, val.length - 2));
          } else if (
            type === INPUT_TYPES.WHOLE_NUMBER &&
            !validateNumber(event.target.value)
          ) {
            setNumberErrorClass('number-error');
            setTimeout(
              () => setNumberErrorClass('no-number-error'),
              DEFAULT_DELAY
            );
          } else {
            onChange(event.target.value);
          }
        },
        type: inputType || getInputType(type),
        placeholder,
        onFocus: (ev) => {
          setFocused(true);
          if (onFocus) {
            onFocus(ev);
          }
        },
        onBlur: (ev) => {
          setFocused(false);
          if (onBlur) {
            onBlur(ev);
          }
          if (type === INPUT_TYPES.EMAIL) {
            if (!validateEmail(ev.target.value)) {
              setEmailErrorClass('email-error');
            } else {
              setEmailErrorClass('no-email-error');
            }
          }
        },
        disabled,
        ...idAttr,
        ...others,
      };

      if (
        [INPUT_TYPES.PRICE, INPUT_TYPES.DECIMAL].includes(type) ||
        [INPUT_TYPES.PERCENTAGE, INPUT_TYPES.DECIMAL].includes(type)
      ) {
        const decimalScale =
          type === INPUT_TYPES.PRICE || type === INPUT_TYPES.PERCENTAGE
            ? { decimalScale: 2, fixedDecimalScale: true }
            : {};

        return (
          <StyledNumberInput
            shrink={isShrunk}
            defaultValue=""
            {...decimalScale}
            {...commonInputProps}
          />
        );
      }

      return <StyledInput shrink={isShrunk} {...commonInputProps} />;
    };

    return (
      <div ref={ref} className={className}>
        <StyledLabelWrapper htmlFor={id}>
          {label && !hideLabel && (
            <StyledLabel
              shrink={focused || isFilled || isAdornedStart}
              {...InputLabelProps}
              {...tooltipProps}
            >
              {label}
            </StyledLabel>
          )}
          <StyledInputWrapper
            focused={focused}
            error={error}
            underline={underline}
            pill={pill}
            disabled={disabled}
            className={`input-wrapper ${numberErrorClass} ${emailErrorClass}`}
            onClick={onClick}
          >
            {type === INPUT_TYPES.PRICE && (
              <StyledInputEndcap position="front">
                {currencyCharacter}
              </StyledInputEndcap>
            )}
            {type === INPUT_TYPES.PERCENTAGE && (
              <StyledInputEndcap position="front">
                {percentageCharacter}
              </StyledInputEndcap>
            )}
            {renderInputField()}
            {!endcap && type === INPUT_TYPES.SEARCH ? (
              <StyledInputEndcap className="search">
                <Icon
                  icon="search"
                  className="search-icon"
                  color="var(--color-background-dark)"
                />
              </StyledInputEndcap>
            ) : null}
            {endcap && type === INPUT_TYPES.TEXT && !hideIcon && (
              <StyledInputEndcap>{endcap}</StyledInputEndcap>
            )}
          </StyledInputWrapper>
        </StyledLabelWrapper>
        {tooltip}
      </div>
    );
  }
);

Input.INPUT_TYPES = INPUT_TYPES;

export default Input;
